import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { LogIn, Loader2, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ username: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    const to = loc.state?.from?.pathname || "/";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await login(form.username, form.password);
      if (r.must_change_password) nav("/change-password", { replace: true });
      else nav(loc.state?.from?.pathname || "/", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) setErr("Account temporaneamente bloccato per troppi tentativi. Riprova fra alcuni minuti.");
      else setErr("Credenziali non valide");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="emmepi-logo-mark">M</div>
          <div>
            <div className="text-lg font-extrabold text-slate-900">emmepigroup</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Service · v1.0</div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Accedi</h1>
        <p className="text-sm text-slate-600 mb-6">Inserisci le tue credenziali per accedere alla piattaforma.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Username (email aziendale)</Label>
            <Input type="email" required autoFocus autoComplete="username"
              value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
              data-testid="login-username" placeholder="nome.cognome@emmepigroup.com" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" required autoComplete="current-password"
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
              data-testid="login-password" />
          </div>
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2" data-testid="login-error"><AlertCircle className="w-4 h-4" /> {err}</div>}
          <Button type="submit" disabled={busy} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="login-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />} Accedi
          </Button>
        </form>
        <div className="mt-6 pt-4 border-t text-xs text-slate-500 flex items-center justify-between">
          <Link to="/forgot-password" className="text-blue-700 hover:underline" data-testid="link-forgot">Password dimenticata?</Link>
          <span>Al primo accesso ti verrà chiesta una nuova password.</span>
        </div>
      </Card>
    </div>
  );
}
